import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Heading,
    VStack,
    useToast,
    Flex,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    useColorMode,
    Button,
    Text,
    Spinner,
    Select,
    Badge,
    AlertDialog,
    AlertDialogOverlay,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogBody,
    AlertDialogFooter,
    FormControl,
    FormLabel,
    Input,
} from '@chakra-ui/react';
import { DownloadIcon } from '@chakra-ui/icons';
import Navigation from '../components/Navigation';
import axios from '../utils/axiosConfig';

const MONTH_NAMES = [
    '', 'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

const STATUS_COLORS = {
    unpaid: 'orange',
    paid: 'green',
    overdue: 'red',
    suspended: 'red',
    disputed: 'purple',
    refunded: 'gray',
    cancelled: 'gray',
};

const AdminInvoicesPage = () => {
    const { colorMode } = useColorMode();
    const toast = useToast();
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('');
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [actionType, setActionType] = useState(null); // 'mark_paid' | 'unsuspend'
    const [actionLoading, setActionLoading] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState('');
    const [transactionNumber, setTransactionNumber] = useState('');
    const cancelRef = React.useRef();

    const fetchInvoices = useCallback(async () => {
        try {
            setLoading(true);
            const params = statusFilter ? `?status=${statusFilter}` : '';
            const response = await axios.get(`/api/admin/invoices${params}`);
            if (response.data.success) {
                setInvoices(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching invoices:', error);
            toast({
                title: 'Error',
                description: 'Failed to fetch invoices.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    }, [statusFilter, toast]);

    useEffect(() => {
        fetchInvoices();
    }, [fetchInvoices]);

    const openDialog = (invoice, type) => {
        setSelectedInvoice(invoice);
        setActionType(type);
        setPaymentMethod('');
        setTransactionNumber('');
    };

    const closeDialog = () => {
        setSelectedInvoice(null);
        setActionType(null);
    };

    const handleMarkPaid = async () => {
        if (!selectedInvoice) return;
        setActionLoading(true);
        try {
            const response = await axios.put(`/api/admin/invoices/${selectedInvoice._id}/mark-paid`, {
                paymentMethod: paymentMethod || undefined,
                transactionNumber: transactionNumber || undefined,
            });
            if (response.data.success) {
                toast({ title: 'Success', description: response.data.message, status: 'success', duration: 5000, isClosable: true });
                await fetchInvoices();
            }
        } catch (error) {
            toast({ title: 'Error', description: error.response?.data?.message || 'Failed.', status: 'error', duration: 3000, isClosable: true });
        } finally {
            setActionLoading(false);
            closeDialog();
        }
    };

    const handleUnsuspend = async () => {
        if (!selectedInvoice) return;
        setActionLoading(true);
        try {
            const response = await axios.put(`/api/admin/invoices/${selectedInvoice._id}/unsuspend`);
            if (response.data.success) {
                toast({ title: 'Success', description: response.data.message, status: 'success', duration: 5000, isClosable: true });
                await fetchInvoices();
            }
        } catch (error) {
            toast({ title: 'Error', description: error.response?.data?.message || 'Failed.', status: 'error', duration: 3000, isClosable: true });
        } finally {
            setActionLoading(false);
            closeDialog();
        }
    };

    const handleCancel = async () => {
        // ... (existing handleCancel code)
        if (!selectedInvoice) return;
        setActionLoading(true);
        try {
            const response = await axios.put(`/api/admin/invoices/${selectedInvoice._id}/cancel`);
            if (response.data.success) {
                toast({ title: 'Cancelled', description: response.data.message, status: 'info', duration: 5000, isClosable: true });
                await fetchInvoices();
            }
        } catch (error) {
            toast({ title: 'Error', description: error.response?.data?.message || 'Failed.', status: 'error', duration: 3000, isClosable: true });
        } finally {
            setActionLoading(false);
            closeDialog();
        }
    };

    const handleDownloadPdf = async (invoice) => {
        try {
            const response = await axios.get(`/api/admin/invoices/${invoice._id}/download`, {
                responseType: 'blob', // Important for receiving binary data
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${invoice.invoiceNumber}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        } catch (error) {
            console.error('Error downloading invoice:', error);
            toast({
                title: 'Download Failed',
                description: 'Could not generate the invoice PDF.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-SG', {
            day: '2-digit', month: 'short', year: 'numeric',
        });
    };

    const formatPeriods = (periods) => {
        if (!periods || periods.length === 0) return '—';
        return periods.map(p => `${MONTH_NAMES[p.month]?.substring(0, 3)} ${p.year}`).join(', ');
    };

    const formatPaymentMethod = (method) => {
        if (!method) return '—';
        const labels = {
            bank_transfer: 'Bank Transfer',
            paynow: 'PayNow',
            stripe: 'Card (Stripe)',
            credit_card: 'Credit Card',
            cash: 'Cash',
            cheque: 'Cheque',
            other: 'Other',
        };
        return labels[method] || method;
    };

    const outstandingTotal = invoices
        .filter(i => ['unpaid', 'overdue', 'suspended'].includes(i.status))
        .reduce((sum, i) => sum + i.totalAmount, 0);

    return (
        <Flex>
            <Navigation />
            <Box flex="1" p={6} maxW="1400px" mx="auto">
                <VStack spacing={6} align="stretch">
                    <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
                        <Heading size="lg" color={colorMode === 'light' ? 'gray.800' : 'white'}>
                            All Invoices (Admin)
                        </Heading>
                        <Flex gap={3} align="center">
                            <Select
                                size="sm"
                                w="160px"
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                bg={colorMode === 'light' ? 'white' : 'gray.700'}
                                color={colorMode === 'light' ? 'gray.800' : 'white'}
                                borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'}
                            >
                                <option value="">All Statuses</option>
                                <option value="unpaid">Unpaid</option>
                                <option value="paid">Paid</option>
                                <option value="overdue">Overdue</option>
                                <option value="suspended">Suspended</option>
                                <option value="cancelled">Cancelled</option>
                            </Select>
                            <Button size="sm" onClick={fetchInvoices} isLoading={loading} colorScheme="blue" variant="outline">
                                Refresh
                            </Button>
                        </Flex>
                    </Flex>

                    {outstandingTotal > 0 && (
                        <Box
                            p={4}
                            bg={colorMode === 'light' ? 'orange.50' : 'orange.900'}
                            borderRadius="md"
                            borderWidth={1}
                            borderColor={colorMode === 'light' ? 'orange.200' : 'orange.700'}
                        >
                            <Text fontWeight="semibold" color={colorMode === 'light' ? 'orange.800' : 'orange.200'}>
                                Outstanding Total: ${outstandingTotal.toFixed(2)}
                            </Text>
                        </Box>
                    )}

                    {loading ? (
                        <Flex justify="center" py={10}><Spinner size="xl" /></Flex>
                    ) : invoices.length === 0 ? (
                        <Box p={8} textAlign="center" bg={colorMode === 'light' ? 'gray.50' : 'gray.700'} borderRadius="md">
                            <Text color={colorMode === 'light' ? 'gray.500' : 'gray.400'}>No invoices found.</Text>
                        </Box>
                    ) : (
                        <Box borderWidth={1} borderRadius="md" borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'} overflowX="auto">
                            <Table variant="simple" size="sm">
                                <Thead bg={colorMode === 'light' ? 'gray.50' : 'gray.700'}>
                                    <Tr>
                                        <Th color={colorMode === 'light' ? 'gray.600' : 'gray.300'}>Invoice #</Th>
                                        <Th color={colorMode === 'light' ? 'gray.600' : 'gray.300'}>User</Th>
                                        <Th color={colorMode === 'light' ? 'gray.600' : 'gray.300'}>Period</Th>
                                        <Th color={colorMode === 'light' ? 'gray.600' : 'gray.300'} isNumeric>Amount ($)</Th>
                                        <Th color={colorMode === 'light' ? 'gray.600' : 'gray.300'}>Status</Th>
                                        <Th color={colorMode === 'light' ? 'gray.600' : 'gray.300'}>Due Date</Th>
                                        <Th color={colorMode === 'light' ? 'gray.600' : 'gray.300'}>Paid</Th>
                                        <Th color={colorMode === 'light' ? 'gray.600' : 'gray.300'}>Payment</Th>
                                        <Th color={colorMode === 'light' ? 'gray.600' : 'gray.300'}>Action</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {invoices.map((inv) => (
                                        <Tr key={inv._id}>
                                            <Td>
                                                <Text fontWeight="medium" fontSize="sm" color={colorMode === 'light' ? 'gray.800' : 'white'}>
                                                    {inv.invoiceNumber}
                                                </Text>
                                            </Td>
                                            <Td>
                                                <Text fontWeight="medium" fontSize="sm" color={colorMode === 'light' ? 'gray.800' : 'white'}>
                                                    {inv.userName}
                                                </Text>
                                                <Text fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.400'}>
                                                    {inv.userEmail}
                                                </Text>
                                            </Td>
                                            <Td>
                                                <Text fontSize="xs" color={colorMode === 'light' ? 'gray.800' : 'white'}>
                                                    {formatPeriods(inv.periods)}
                                                </Text>
                                            </Td>
                                            <Td isNumeric fontWeight="semibold" fontSize="sm" color={colorMode === 'light' ? 'gray.800' : 'white'}>
                                                {inv.totalAmount?.toFixed(2)}
                                            </Td>
                                            <Td>
                                                <Badge colorScheme={STATUS_COLORS[inv.status] || 'gray'} variant="subtle">
                                                    {inv.status}
                                                </Badge>
                                            </Td>
                                            <Td fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.400'}>
                                                {formatDate(inv.dueDate)}
                                            </Td>
                                            <Td fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.400'}>
                                                {formatDate(inv.paidAt)}
                                            </Td>
                                            <Td fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.400'}>
                                                {formatPaymentMethod(inv.paymentMethod)}
                                                {inv.transactionNumber && (
                                                    <Text fontSize="xs">{inv.transactionNumber}</Text>
                                                )}
                                            </Td>
                                            <Td>
                                                <Flex gap={1} direction="column">
                                                    {['unpaid', 'overdue'].includes(inv.status) && (
                                                        <Button
                                                            size="xs"
                                                            colorScheme="green"
                                                            onClick={() => openDialog(inv, 'mark_paid')}
                                                        >
                                                            Mark Paid
                                                        </Button>
                                                    )}
                                                    {inv.status === 'suspended' && (
                                                        <>
                                                            <Button
                                                                size="xs"
                                                                colorScheme="green"
                                                                onClick={() => openDialog(inv, 'mark_paid')}
                                                            >
                                                                Mark Paid
                                                            </Button>
                                                            <Button
                                                                size="xs"
                                                                colorScheme="orange"
                                                                variant="outline"
                                                                onClick={() => openDialog(inv, 'unsuspend')}
                                                            >
                                                                Unsuspend
                                                            </Button>
                                                        </>
                                                    )}
                                                    {inv.status !== 'paid' && inv.status !== 'cancelled' && (
                                                        <Button
                                                            size="xs"
                                                            colorScheme="red"
                                                            variant="ghost"
                                                            onClick={() => openDialog(inv, 'cancel')}
                                                        >
                                                            Cancel
                                                        </Button>
                                                    )}
                                                    <Button
                                                        size="xs"
                                                        leftIcon={<DownloadIcon />}
                                                        onClick={() => handleDownloadPdf(inv)}
                                                        variant="ghost"
                                                        colorScheme="blue"
                                                    >
                                                        PDF
                                                    </Button>
                                                </Flex>
                                            </Td>
                                        </Tr>
                                    ))}
                                </Tbody>
                            </Table>
                        </Box>
                    )}
                </VStack>
            </Box>

            {/* Mark Paid / Unsuspend Dialog */}
            <AlertDialog
                isOpen={!!selectedInvoice}
                leastDestructiveRef={cancelRef}
                onClose={closeDialog}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent bg={colorMode === 'light' ? 'white' : 'gray.800'}>
                        <AlertDialogHeader color={colorMode === 'light' ? 'gray.800' : 'white'}>
                            {actionType === 'mark_paid' ? 'Mark Invoice as Paid' : actionType === 'unsuspend' ? 'Unsuspend Invoice' : 'Cancel Invoice'}
                        </AlertDialogHeader>
                        <AlertDialogBody color={colorMode === 'light' ? 'gray.600' : 'gray.300'}>
                            {selectedInvoice && (
                                <>
                                    <Text>
                                        <strong>{selectedInvoice.invoiceNumber}</strong> — {selectedInvoice.userName} — ${selectedInvoice.totalAmount?.toFixed(2)}
                                    </Text>
                                    {actionType === 'mark_paid' && (
                                        <Box mt={4}>
                                            <FormControl mb={3}>
                                                <FormLabel fontSize="sm">Payment Method</FormLabel>
                                                <Select
                                                    size="sm"
                                                    placeholder="Select method"
                                                    value={paymentMethod}
                                                    onChange={(e) => setPaymentMethod(e.target.value)}
                                                >
                                                    <option value="bank_transfer">Bank Transfer</option>
                                                    <option value="paynow">PayNow</option>
                                                    <option value="credit_card">Credit Card</option>
                                                    <option value="stripe">Stripe</option>
                                                    <option value="cash">Cash</option>
                                                    <option value="cheque">Cheque</option>
                                                    <option value="other">Other</option>
                                                </Select>
                                            </FormControl>
                                            <FormControl>
                                                <FormLabel fontSize="sm">Transaction Number (Optional)</FormLabel>
                                                <Input
                                                    size="sm"
                                                    placeholder="e.g. TXN-12345"
                                                    value={transactionNumber}
                                                    onChange={(e) => setTransactionNumber(e.target.value)}
                                                />
                                            </FormControl>
                                        </Box>
                                    )}
                                    {actionType === 'unsuspend' && (
                                        <Text mt={2} fontSize="sm">
                                            This will restore the user's account access. The invoice will revert to <strong>overdue</strong> status until paid.
                                        </Text>
                                    )}
                                    {actionType === 'cancel' && (
                                        <Text mt={2} fontSize="sm" color="red.500">
                                            ⚠️ This will mark the invoice as <strong>cancelled</strong> and revert all linked billing records back to <strong>unbilled</strong> so they can be billed again.
                                        </Text>
                                    )}
                                </>
                            )}
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={closeDialog}>Cancel</Button>
                            <Button
                                colorScheme={actionType === 'cancel' ? 'red' : actionType === 'mark_paid' ? 'green' : 'orange'}
                                onClick={actionType === 'mark_paid' ? handleMarkPaid : actionType === 'unsuspend' ? handleUnsuspend : handleCancel}
                                ml={3}
                                isLoading={actionLoading}
                            >
                                {actionType === 'mark_paid' ? 'Mark Paid' : actionType === 'unsuspend' ? 'Unsuspend' : 'Confirm Cancel'}
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </Flex>
    );
};

export default AdminInvoicesPage;
